import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/shared/contexts/AuthContext';
import {
  Permission,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
} from '@/shared/types/permissions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  adminOnly?: boolean;
  allowedRoles?: string[];
  // Novas props para permissões granulares
  requiredPermission?: Permission;
  requiredAnyPermissions?: Permission[];
  requiredAllPermissions?: Permission[];
}

export function ProtectedRoute({
  children,
  adminOnly = false,
  allowedRoles,
  requiredPermission,
  requiredAnyPermissions,
  requiredAllPermissions,
}: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const getPeopleCorePath = () => '/people-core/dashboard';
  const getAccessDeniedPath = () => {
    if (user?.role === 'CLIENT') {
      return '/client/dashboard';
    }
    if (user?.role === 'COLLABORATOR' || user?.role === 'GESTOR') {
      return getPeopleCorePath();
    }
    return '/';
  };

  // Check adminOnly (legacy)
  if (adminOnly && user?.role !== 'ADMIN') {
    return <Navigate to={getAccessDeniedPath()} replace />;
  }

  // Check allowedRoles (legacy)
  if (allowedRoles && user?.role && !allowedRoles.includes(user.role)) {
    return <Navigate to={getAccessDeniedPath()} replace />;
  }

  // Check requiredPermission (nova validação granular)
  if (
    requiredPermission &&
    !hasPermission(user?.role || '', user?.permissions, requiredPermission)
  ) {
    return <Navigate to={getAccessDeniedPath()} replace />;
  }

  // Check requiredAnyPermissions (qualquer uma das permissões)
  if (
    requiredAnyPermissions &&
    !hasAnyPermission(user?.role || '', user?.permissions, requiredAnyPermissions)
  ) {
    return <Navigate to={getAccessDeniedPath()} replace />;
  }

  // Check requiredAllPermissions (todas as permissões)
  if (
    requiredAllPermissions &&
    !hasAllPermissions(user?.role || '', user?.permissions, requiredAllPermissions)
  ) {
    return <Navigate to={getAccessDeniedPath()} replace />;
  }

  return <>{children}</>;
}
